import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface UserActivity {
  id: string;
  type: 'match' | 'tournament' | 'league' | 'registration';
  title: string;
  date: string;
  location: string;
  result?: 'Victoria' | 'Derrota' | 'En curso' | 'Registrado';
  score?: string;
  opponent?: string;
  sport: string;
  eventId?: string;
}

export function useUserActivity(userId?: string) {
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserActivity = async () => {
    try {
      setLoading(true);
      
      // Get target user ID - either provided or current user
      let targetUserId = userId;
      if (!targetUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        targetUserId = user.id;
      }

      // Get user registrations with event details
      const { data: registrations } = await supabase
        .from('registrations')
        .select(`
          *,
          events(*)
        `)
        .eq('user_id', targetUserId)
        .order('registered_at', { ascending: false })
        .limit(10);

      // Get user matches with results
      const { data: matches } = await supabase
        .from('matches')
        .select(`
          *,
          events(*),
          match_results(*)
        `)
        .or(`player1_id.eq.${targetUserId},player2_id.eq.${targetUserId}`)
        .order('created_at', { ascending: false })
        .limit(10);

      const activities: UserActivity[] = [];

      // Process registrations
      if (registrations) {
        registrations.forEach(reg => {
          if (reg.events) {
            const event = reg.events;
            activities.push({
              id: `reg-${reg.id}`,
              type: event.event_type === 'tournament' ? 'tournament' : 'league',
              title: `Registro en ${event.title}`,
              date: reg.registered_at,
              location: event.location,
              result: reg.status === 'confirmed' ? 'Registrado' : 'En curso',
              sport: event.sport,
              eventId: event.id
            });
          }
        });
      }

      // Process matches
      if (matches) {
        matches.forEach(match => {
          if (match.events && match.match_results && match.match_results.length > 0) {
            const event = match.events;
            const result = match.match_results[0];
            const isWinner = result.winner_id === targetUserId;
            const opponent = match.player1_id === targetUserId ? 'Jugador 2' : 'Jugador 1';
            
            activities.push({
              id: `match-${match.id}`,
              type: 'match',
              title: `Partido en ${event.title}`,
              date: result.completed_at || match.created_at,
              location: match.location || event.location,
              result: isWinner ? 'Victoria' : 'Derrota',
              score: `${result.player1_score}-${result.player2_score}`,
              opponent,
              sport: event.sport,
              eventId: event.id
            });
          }
        });
      }

      // Sort all activities by date (most recent first)
      activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setActivities(activities.slice(0, 10)); // Keep only the 10 most recent
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserActivity();
  }, [userId]);

  return { 
    activities, 
    loading, 
    error, 
    refetch: fetchUserActivity 
  };
}